import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchPublicInfo, fetchPublicKey, postAlert } from '../../../Actions/PublicInfoActions';
import { getPreSignedUrl } from '../../../Actions/FormActions';
import { useToast } from '../../../components/Toast/ToastContext';
import PdfViewer from '../PDFViewer/PDFViewer';

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const LabelValue = ({ label, value }) => (
  <div className="mb-3">
    <div className="text-sm text-black">{label}</div>
    <div className="text-lg font-extrabold text-black break-words">{value || '-'}</div>
  </div>
);

const PublicInfo = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const { showToast } = useToast();

  // Persist IP and card id for optional re-send after user grants location permission
  const [ipAddress, setIpAddress] = useState('');
  const [cardIdForAlert, setCardIdForAlert] = useState('');
  const [geoPermission, setGeoPermission] = useState('unknown'); // 'granted' | 'denied' | 'prompt' | 'unknown'
  const [showGeoPrompt, setShowGeoPrompt] = useState(false);
  const [isViewingPrescription, setIsViewingPrescription] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const scrollContainerRef = useRef(null);
  const prescriptionSectionRef = useRef(null);
  const disabilitySectionRef = useRef(null);
  const quickActionRefs = useRef([]);
  const [liveRegionMessage, setLiveRegionMessage] = useState('');
  const [expiryAt, setExpiryAt] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const pdfObjectUrlRef = useRef('');

  const checkGeoPermission = async () => {
    try {
      if (navigator?.permissions?.query) {
        const status = await navigator.permissions.query({ name: 'geolocation' });
        setGeoPermission(status.state);
        setShowGeoPrompt(status.state !== 'granted');
        // Keep UI in sync if user changes site setting manually
        status.onchange = () => {
          setGeoPermission(status.state);
          setShowGeoPrompt(status.state !== 'granted');
        };
      } else {
        // Permissions API not available; only show prompt until we succeed once
        setGeoPermission('unknown');
        setShowGeoPrompt(true);
      }
    } catch (_) {
      setGeoPermission('unknown');
      setShowGeoPrompt(true);
    }
  };

  const handleEnableLocation = () => {
    if (!('geolocation' in navigator)) {
      showToast('Geolocation is not supported by this browser', 'error', 4000);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = typeof pos?.coords?.latitude === 'number' ? pos.coords.latitude : null;
        const lon = typeof pos?.coords?.longitude === 'number' ? pos.coords.longitude : null;
        if (lat != null && lon != null && cardIdForAlert && ipAddress) {
          postAlert(ipAddress, cardIdForAlert, lat, lon);
          showToast('Location captured. Alert updated with your coordinates.', 'success', 3500);
        } else {
          showToast('Location captured, but missing context to update alert.', 'warning', 3500);
        }
        setShowGeoPrompt(false);
      },
      (err) => {
        // 1: PERMISSION_DENIED, 2: POSITION_UNAVAILABLE, 3: TIMEOUT
        if (err?.code === 1) {
          showToast('Permission denied. Please allow location in site settings.', 'error', 4500);
        } else if (err?.code === 2) {
          showToast('Location unavailable. Try again later.', 'error', 4000);
        } else {
          showToast('Timed out trying to get location. Try again.', 'warning', 4000);
        }
        setShowGeoPrompt(true);
      },
      { enableHighAccuracy: false, timeout: 7000, maximumAge: 300000 }
    );
  };

  useEffect(() => {
    const privateKeyFromQuery = query.get('private_key');
    const publicKey = query.get('public_key');
    // Handle case where query string is a bare key like /view?abcdef (no name=value)
    let bareQueryKey = '';
    if (!privateKeyFromQuery && !publicKey) {
      const rawSearch = window.location.search || '';
      if (rawSearch.startsWith('?') && !rawSearch.includes('=')) {
        bareQueryKey = decodeURIComponent(rawSearch.slice(1));
      }
    }

    async function run() {
      try {
        // Flow 1: /view/:privateKey OR /view?{key} OR /view?private_key=...
        if (privateKeyFromQuery || params.privateKey || bareQueryKey) {
          const privateKey = privateKeyFromQuery || params.privateKey || bareQueryKey;
          const res = await fetchPublicKey(privateKey);
          if (res?.status !== 200 || res?.data?.success !== true || !res?.data?.data?.public_key) {
            throw new Error(res?.data?.message || 'Failed to resolve public key');
          }
          const resolved = res.data.data.public_key;
          navigate(`/view?public_key=${encodeURIComponent(resolved)}`, { replace: true });
          return;
        }

        // Flow 2: /view?public_key=...
        if (publicKey) {
          const res = await fetchPublicInfo(publicKey);
          if (res?.status !== 200 || res?.data?.success !== true) {
            throw new Error(res?.data?.message || 'Failed to load info');
          }
          setData(res.data.data || {});
          setError('');

          // Prepare IP and card id for potential alert on user action (no auto-alert on load)
          try {
            const fetchedIp = (await fetch('https://api.ipify.org?format=json').then(r => r.json()).catch(() => ({}))).ip || '';
            const cardId = res?.data?.data?.card_id || '';
            setIpAddress(fetchedIp);
            setCardIdForAlert(cardId);
          } catch (_) {
            // noop
          }
        }
      } catch (e) {
        setError(e?.message || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    }

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.privateKey, query.toString()]);

  // Start 10-minute expiry once data is loaded
  useEffect(() => {
    if (!data) return;
    const now = Date.now();
    const target = now + 10 * 60 * 1000; // 10 minutes
    setExpiryAt(target);
    const timeoutId = setTimeout(() => {
      setIsExpired(true);
    }, target - now);
    return () => clearTimeout(timeoutId);
  }, [data]);

  // Show floating scroll-to-top button after user scrolls down a bit
  useEffect(() => {
    const el = scrollContainerRef.current;
    const onScroll = () => {
      try {
        const currentTop = el ? el.scrollTop : (window?.scrollY || 0);
        setShowScrollTop(currentTop > 20);
      } catch (_) {
        setShowScrollTop(false);
      }
    };
    onScroll();
    if (el) {
      el.addEventListener('scroll', onScroll, { passive: true });
      return () => el.removeEventListener('scroll', onScroll);
    } else {
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
    }
  }, []);

  // Cleanup any created object URL for PDF
  useEffect(() => {
    return () => {
      try {
        if (pdfObjectUrlRef.current) {
          URL.revokeObjectURL(pdfObjectUrlRef.current);
          pdfObjectUrlRef.current = '';
        }
      } catch (_) {}
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white shadow rounded p-6">
          <div className="text-red-600 font-medium mb-2">Error</div>
          <div className="text-gray-800">{error}</div>
        </div>
      </div>
    );
  }

  // Block access after expiry
  if (isExpired) {
    return (
      <div className="min-h-screen w-full bg-[#533377] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow p-6 text-center">
          <div className="text-[#E31F1F] font-bold text-lg mb-2">Session expired</div>
          <p className="text-gray-800 mb-5">
            For privacy, this page expires after 10 minutes. Please exit or tap the NFC card again to reopen this page.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="px-4 py-2 rounded-full bg-gray-200 text-black font-semibold"
            >
              Exit Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const emergencyContacts = Array.isArray(data?.emergency_contacts) ? data.emergency_contacts : [];
  const primaryContact = emergencyContacts[0] || {};
  const secondaryContact = emergencyContacts[1] || {};

  const toFeetInches = (cm) => {
    const numeric = Number(cm);
    if (!numeric || Number.isNaN(numeric)) return '';
    const totalInches = numeric / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return `${feet} feet ${inches} inches`;
  };

  const scrollToPrescriptionSection = () => {
    try {
      const target = prescriptionSectionRef.current;
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setIsViewingPrescription(true);
        return;
      }
    } catch (_) {
      // no-op, fallback to opening
    }
  };

  const scrollToDisabilitySection = () => {
    try {
      const target = disabilitySectionRef.current;
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    } catch (_) {
      // no-op, fallback to opening
    }
  };

  const sendAlertWithBestLocation = () => {
    if (!(ipAddress && data?.card_id)) {
      showToast('Unable to send alert at the moment', 'error');
      return;
    }
    if ('geolocation' in navigator) {
      let completed = false;
      const onSuccess = (pos) => {
        if (completed) return;
        completed = true;
        const lat = typeof pos?.coords?.latitude === 'number' ? pos.coords.latitude : null;
        const lon = typeof pos?.coords?.longitude === 'number' ? pos.coords.longitude : null;
        if (lat != null && lon != null) {
          postAlert(ipAddress, data.card_id, lat, lon);
        } else {
          postAlert(ipAddress, data.card_id);
        }
        showToast('Alert sent to emergency contacts.', 'success');
        setLiveRegionMessage('Medical emergency alert activated. Emails and notifications sent.');
      };
      const onError = () => {
        if (completed) return;
        completed = true;
        postAlert(ipAddress, data.card_id);
        showToast('Alert sent to emergency contacts.', 'success');
        setLiveRegionMessage('Medical emergency alert activated. Emails and notifications sent.');
      };
      try {
        navigator.geolocation.getCurrentPosition(
          onSuccess,
          onError,
          { enableHighAccuracy: false, timeout: 6000, maximumAge: 300000 }
        );
        return;
      } catch (_) {
        // fall through to IP-only
      }
    }
    postAlert(ipAddress, data.card_id);
    showToast('Alert sent to emergency contacts.', 'success');
    setLiveRegionMessage('Medical emergency alert activated. Emails and notifications sent.');
  };

  const openPdfInDialogFromBase64 = (base64) => {
    try {
      if (!base64) return;
      // Convert base64 → binary → Blob
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
  
      // Revoke old URL if exists
      if (pdfObjectUrlRef.current) {
        try { URL.revokeObjectURL(pdfObjectUrlRef.current); } catch (_) {}
      }
  
      // Create blob URL
      const objectUrl = URL.createObjectURL(blob);
      pdfObjectUrlRef.current = objectUrl;
      setPdfUrl(objectUrl);
      setIsPdfDialogOpen(true);
    } catch (err) {
      showToast('Unable to open PDF', 'error');
    }
  };

  const openPdfInDialogFromUrl = (url) => {
    try {
      if (!url) return;
      // Revoke old object URL if exists (we only revoke blob URLs we created)
      if (pdfObjectUrlRef.current) {
        try { URL.revokeObjectURL(pdfObjectUrlRef.current); } catch (_) {}
        pdfObjectUrlRef.current = '';
      }
      setPdfUrl(url);
      setIsPdfDialogOpen(true);
    } catch (_) {
      showToast('Unable to open PDF', 'error');
    }
  };


  const scrollToTop = () => {
    const el = scrollContainerRef.current;
    if (el) {
      try {
        el.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (_) {
        el.scrollTop = 0;
      }
      return;
    }
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (_) {
      window.scrollTo(0, 0);
    }
  };

  const handleQuickActionsKeyDown = (e) => {
    const isArrowLeft = e.key === 'ArrowLeft';
    const isArrowRight = e.key === 'ArrowRight';
    if (!isArrowLeft && !isArrowRight) return;
    const items = quickActionRefs.current.filter(Boolean);
    if (!items.length) return;
    e.preventDefault();
    const activeIndex = items.indexOf(document.activeElement);
    let nextIndex = 0;
    if (isArrowRight) {
      nextIndex = activeIndex >= 0 ? (activeIndex + 1) % items.length : 0;
    } else {
      nextIndex = activeIndex >= 0 ? (activeIndex - 1 + items.length) % items.length : items.length - 1;
    }
    items[nextIndex]?.focus();
  };

  return (
    <div ref={scrollContainerRef} className="scroll-enabled w-full bg-[#533377]">
      <a href="#patient-details" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:bg-white focus:text-black focus:px-3 focus:py-2 focus:rounded focus:shadow" aria-label="Skip to Patient Details">
        Skip to content
      </a>
      <div id="alert-live" aria-live="assertive" role="status" className="sr-only">{liveRegionMessage}</div>
      <div className="max-w-5xl mx-auto px-4 py-6 pb-28 md:pb-32">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-6">
          <img src="/Assets/Images/logo.svg" alt="Project Sunflower Lanyard logo with a bright yellow half sunflower icon next to the text, on a purple background." className="h-12 w-auto" />
          <button
            type="button"
            onClick={sendAlertWithBestLocation}
            aria-label="Click to trigger emergency alert"
            className="inline-flex items-center gap-2 bg-[#E31F1F] hover:bg-[#b02423] text-white font-bold px-8 md:px-10 py-2 rounded-full"
          >
            <img src="/Assets/Images/AlertButtonIcon.svg" alt="" aria-hidden="true" role="presentation" className="w-4 h-4" />
            Alert
          </button>
        </div>

        {/* Intro text */}
        <div className="mb-6 md:mb-8">
          <p className="text-white text-base font-bold md:text-lg">
            This page holds Confidential Health Information about <span className="font-bold text-[#F9D50A]">{data?.full_name || 'the user'}</span>.
          </p>
          <p className="text-white/90 font-semibold text-sm md:text-base mt-3">For emergencies, please press the Alert button.</p>
        </div>

        {/* Cards */}
        <div className="space-y-5 md:space-y-6">
          {/* Patient Details */}
          <div id="patient-details" tabIndex="-1" className="rounded-xl p-4 md:p-6 bg-[#F3EDF9]">
            <div className="text-sm font-extrabold mb-4 text-[#118B91]" role="heading" aria-level="2">Patient Details</div>
            <LabelValue label="Name" value={data?.full_name} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LabelValue label="Medical Condition Name" value={data?.medical_condition?.name} />
              <LabelValue label="Doctor's Name" value={data?.medical_condition?.doctor_name} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-1">
              <LabelValue label="Age" value={data?.age ? `${data.age} yrs` : '-'} />
              <LabelValue label="Gender" value={data?.gender} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LabelValue label="Height" value={data?.height ? `${data.height} cm/ ${toFeetInches(data.height)}` : '-'} />
              <LabelValue label="Weight" value={data?.weight ? `${data.weight} kg` : '-'} />
            </div>
            <LabelValue label="Blood group" value={data?.blood_group} />
            <LabelValue label="Allergies" value={data?.medical_condition?.allergies} />
          </div>

          {/* Medical Notes */}
          <div className="rounded-xl p-4 md:p-6 bg-[#F3EDF9]">
            <div className="text-sm font-extrabold mb-2 text-[#118B91]" role="heading" aria-level="2">Medical Notes</div>
            <div className="text-md font-medium text-black whitespace-pre-line">{data?.medical_condition?.notes || '-'}</div>
          </div>

          {/* Prescription */}
          {(() => {
            const med = data?.medical_condition?.certificate;
            const medContentType = typeof med?.content_type === 'string' ? med.content_type.toLowerCase() : '';
            const medIsImage = medContentType.startsWith('image/') && med?.content_base64;
            const medIsPdf = (med?.content_base64 || med?.presigned_url) && medContentType.includes('pdf');
            const medHasBase64 = !!med?.content_base64;
            if (!med) return null;
            return (
              <div ref={prescriptionSectionRef} className="rounded-xl p-4 md:p-6 bg-[#F3EDF9]">
                <div className="text-sm font-extrabold mb-4 text-[#118B91]" role="heading" aria-level="2">Prescription</div>
                {medIsImage ? (
                  <img
                    src={`data:${med.content_type};base64,${med.content_base64}`}
                    alt="Uploaded prescription image — scanned doctors' notes."
                    className="w-full max-h-[640px] object-contain rounded-lg border bg-white"
                  />
                ) : medIsPdf ? (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        if (med?.presigned_url) {
                          openPdfInDialogFromUrl(med.presigned_url);
                        } else {
                          openPdfInDialogFromBase64(med.content_base64);
                        }
                      }}
                      aria-label="Open prescription PDF in full screen"
                      className="px-4 py-2 rounded-full bg-[#753ABF] text-white font-semibold"
                    >
                      View Prescription
                    </button>
                  </div>
                ) : (
                  medHasBase64 ? (
                    <a
                      href={`data:${med.content_type || 'application/octet-stream'};base64,${med.content_base64}`}
                      download={med?.original_name || med?.file_name || 'prescription'}
                      className="inline-block px-4 py-2 rounded-full bg-[#753ABF] text-white font-semibold"
                      aria-label="Download prescription"
                    >
                      Download Prescription
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          if (med?.file_key) {
                            const response = await getPreSignedUrl(med.file_key);
                            if (response?.success && response?.data?.presigned_url) {
                              window.open(response.data.presigned_url, '_blank');
                            } else {
                              showToast('Unable to open prescription', 'error');
                            }
                          } else {
                            showToast('No file available to open', 'error');
                          }
                        } catch (_) {
                          showToast('Unable to open prescription', 'error');
                        }
                      }}
                      aria-label="View medical prescription"
                      className="px-4 py-2 rounded-full bg-[#753ABF] text-white font-semibold"
                    >
                      View Prescription
                    </button>
                  )
                )}
              </div>
            );
          })()}

          {/* Emergency contacts */}
          <div className="rounded-xl p-4 md:p-6 bg-[#F3EDF9]">
            <div className="text-sm font-extrabold mb-4 text-[#118B91]" role="heading" aria-level="2">Emergency contact Details</div>

            {emergencyContacts.filter(Boolean).map((contact, idx) => (
              <div key={idx} className={idx === 0 ? '' : 'mt-5 pt-5 border-t border-[#D8CDEE]'}>
                <div className="text-sm text-[#753ABF] mb-3">Emergency contact {idx + 1}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 items-start">
                  <div>
                    <div className="text-md text-black">Name</div>
                    <div className="text-lg font-extrabold text-black break-words">{contact?.name || '-'}</div>
                  </div>
                  <div>
                    <div className="text-md text-black">Relationship</div>
                    <div className="text-lg font-extrabold text-black">{contact?.relationship || '-'}</div>
                  </div>
                  <div>
                    <div className="text-md text-black">Contact</div>
                    <div className="flex items-center gap-2 text-lg font-extrabold text-black min-w-0">
                      <span
                        className="flex-1 truncate"
                        title={contact?.mobile ? `+91-${contact.mobile}` : '-'}
                      >
                        +91-{contact?.mobile || '-'}
                      </span>
                      <button
                        type="button"
                        aria-label="Call emergency contact"
                        className='flex-shrink-0 cursor-pointer p-2 bg-[#753ABF] rounded-full w-fit min-w-fit flex items-center justify-center'
                        onClick={() => {
                          if (contact?.mobile) {
                            window.location.href = `tel:${contact.mobile}`;
                          } else {
                            showToast('Emergency contact number not available', 'warning');
                          }
                        }}
                      >
                        <img src="/Assets/Images/Calling.svg" alt="" aria-hidden="true" role="presentation" className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div className="text-xs text-black mt-5">If you cannot reach the emergency contact, please call our helpline’s number at <a href="tel:+918287026805" className="text-black" aria-label="Call PSL helpline at plus nine one eight two eight seven zero two six eight zero five">+91-82870 26805</a></div>
          </div>
        </div>
      </div>

      {/* Removed redundant visually-hidden duplicate buttons that confused screen readers */}

      {/* Floating bottom bar */}
      <div className="fixed bottom-4 left-0 right-0 z-50">
        <div className="mx-auto px-4 max-w-md md:max-w-xl">
          <div className="bg-black/100 text-white rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.45)] px-6 py-4">
            <div className="grid grid-cols-3 gap-4" role="toolbar" aria-label="Quick actions" onKeyDown={handleQuickActionsKeyDown}>
              <button
                type="button"
                onClick={() => {
                  if (primaryContact?.mobile) {
                    window.location.href = `tel:${primaryContact.mobile}`;
                  } else {
                    showToast('Emergency contact number not available', 'warning');
                  }
                }}
                aria-label="Click to call emergency contact"
                ref={(el) => { quickActionRefs.current[0] = el; }}
                className="flex flex-col items-center justify-center gap-2 text-center hover:opacity-90"
              >
                <img src="/Assets/Images/EmergencyContact.svg" alt="" aria-hidden="true" role="presentation" className="w-7 h-7 md:w-8 md:h-8" />
                <span className="text-xs md:text-xs leading-tight">Emergency<br />Contact</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  sendAlertWithBestLocation();
                }}
                aria-label="Click to trigger medical emergency alert"
                ref={(el) => { quickActionRefs.current[1] = el; }}
                className="flex flex-col items-center justify-center gap-2 text-center hover:opacity-90"
              >
                <img src="/Assets/Images/MedicalEmergencyAlert.svg" alt="" aria-hidden="true" role="presentation" className="w-7 h-7 md:w-8 md:h-8" />
                <span className="text-xs md:text-xs leading-tight">Medical<br />Emergency Alert</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  const med = data?.medical_condition?.certificate;
                  const type = typeof med?.content_type === 'string' ? med.content_type.toLowerCase() : '';
                  if (type.includes('pdf')) {
                    if (med?.presigned_url) {
                      openPdfInDialogFromUrl(med.presigned_url);
                      return;
                    }
                    if (med?.content_base64) {
                      openPdfInDialogFromBase64(med.content_base64);
                      return;
                    }
                  }
                  scrollToPrescriptionSection();
                }}
                aria-label="Click to view medical prescription"
                ref={(el) => { quickActionRefs.current[2] = el; }}
                className="flex flex-col items-center justify-center gap-2 text-center hover:opacity-90"
              >
                <img src="/Assets/Images/SeePrescription.svg" alt="" aria-hidden="true" role="presentation" className="w-7 h-7 md:w-8 md:h-8" />
                <span className="text-xs md:text-xs leading-tight">See<br />Prescription</span>
              </button>
            </div>
          </div>
        </div>
      </div>

    {/* Full-screen PDF dialog */}
    {isPdfDialogOpen && (<PdfViewer
            pdfUrl={pdfUrl}
            open={isPdfDialogOpen}
            onClose={() => setIsPdfDialogOpen(false)}
            page={1}
          />)}
    </div>
  );
};

export default PublicInfo;


